import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element for the cart gift-message option.
 *
 * Saves the message to `cart.note` and flags the order with the
 * `Gift order: Yes` attribute via the Cart Update API. Unchecking the box
 * clears both. Modeled on `assets/cart-note.js`.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} checkbox
 * @property {HTMLElement} fields
 * @property {HTMLTextAreaElement} message
 * @property {HTMLElement} counter
 *
 * @extends {Component<Refs>}
 */
class CartGiftNote extends Component {
  requiredRefs = ['checkbox', 'fields', 'message', 'counter'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#updateCounter();
  }

  // Keep the counter in sync when the cart re-renders (Section Rendering morph).
  updatedCallback() {
    super.updatedCallback();
    this.#updateCounter();
  }

  /**
   * Shows/hides the message field and saves or clears the gift state.
   * @param {Event} event
   */
  toggleGift = (event) => {
    const checked = this.refs.checkbox.checked;
    this.refs.fields.hidden = !checked;

    if (checked) {
      this.refs.message.focus();
      this.#save(this.refs.message.value, true, event);
    } else {
      this.refs.message.value = '';
      this.#updateCounter();
      this.#save('', false, event);
    }
  };

  /**
   * Saves the gift message as the cart note (debounced).
   * @param {InputEvent} event
   */
  updateMessage = debounce((event) => {
    if (!(event.target instanceof HTMLTextAreaElement)) return;
    this.#updateCounter();
    this.#save(event.target.value, true, event);
  }, 300);

  /** Updates the "used/max" character counter. */
  #updateCounter() {
    const max = Number(this.dataset.maxLength) || 250;
    const used = this.refs.message.value.length;
    this.refs.counter.textContent = `${used}/${max}`;
  }

  /**
   * Writes the note + gift attribute to the cart, aborting any in-flight save.
   * @param {string} note
   * @param {boolean} isGift
   * @param {Event} [event]
   */
  async #save(note, isGift, event) {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      // An empty-string attribute value removes the attribute from the cart.
      const config = fetchConfig('json', {
        body: JSON.stringify({
          note,
          attributes: { 'Gift order': isGift ? 'Yes' : '' },
        }),
      });

      await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });
    } catch (error) {
      // Aborted or network error — the next input will retry.
    } finally {
      this.#activeFetch = null;
      if (event) cartPerformance.measureFromEvent('gift-note-update:user-action', event);
    }
  }
}

if (!customElements.get('cart-gift-note')) {
  customElements.define('cart-gift-note', CartGiftNote);
}
